<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :customBreadcrumbs="{
        currentArea: t('credits.myCredits.amortization.breadcrumbs.currentArea'),
        menuParent: t('credits.myCredits.amortization.breadcrumbs.menuParent')
    }"
    :feedbackOperationData="headerOperationData"
    :activeStep="activeStep"
    :showStepper="initialFetches && (activeStep <= stepperParams.length)"
    :stepperParams="stepperParams"
    :showFooter="initialFetches && (activeStep <= stepperParams.length)"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :scrollValue="scrollValue"
    :tokenTitleAppend="t('credits.myCredits.amortization.tokenTitleAppend')"
	@challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, amortizeCreditAccountHandler)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Content -->
        <component
        v-if="!showLoader && !applyErrorState && getAccountsBalances.length"
        :is="getActiveStepComponent"
        :accountFormData="accountFormData"
        :accountsBalances="getAccountsBalances"
        :creditInfo="creditInfo"
		:financialPlan="props.creditInfo.financialPlan"
        :responseData="responseData"
        @scrollTo="scrollValue = $event"
        @updateData="accountFormDataChangeHandler"/>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <toasts-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* props received
@selectedCreditAccount { Object } - data received from credit consult
@creditInto {Object} - object with amortization amounts
*/
const props = defineProps({
	selectedCreditAccount: Object,
	creditInfo: Object
})

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestAccountsBalances, amortizeCreditAccount, partialAmortizeCreditAccount, clearFeatureStates } = mapActions(moduleMapper.CREDITS_MY_CREDITS_AMORTIZATION)
const { getAccountsBalances } = mapGetters(moduleMapper.CREDITS_MY_CREDITS_AMORTIZATION)
const { getMobileAppState } = mapGetters()

/* Generic Data */
const initialFetches = ref(true)
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const headerOperationData = ref(null)
const applyErrorState = ref(false)
const showOperationsButtons = ref(true)
const responseData = ref(null)

const amortizationTypeHandler = () => {
	accountFormData.value.amortizationType === 'total' ? amortizeCreditAccountHandler() : partialAmortizeCreditAccountHandler()
}

/*************************************************
*                    Steps Data                  *
*************************************************/
const activeStep = ref(1)
const scrollValue = ref(null)

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const stepperParams = ref([
	{ description: t('credits.myCredits.amortization.stepper.data') },
	{ description: t('credits.myCredits.amortization.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
	const stepNumber = activeStep.value
	return defineAsyncComponent(() => import('@/components/features/credits/amortization/Step' + stepNumber))
})

/*************************************************
*                Form Logic Data                 *
*************************************************/
const accountFormData = ref({
	accountNumber: props.selectedCreditAccount.accountNumber,
	creditAmount: props.creditInfo.loanInitialValue,
	debtAmount: props.creditInfo.loanLeftToPay,
	amortizationType: 'total',
	numberOfSettlements: 1,
	numberOfRemainingInstallments: props.creditInfo.numberOfRemainingInstallments,
	loanInterestRate: props.creditInfo.loanInterestRate,
	currency: props.selectedCreditAccount.currency
})

const accountFormDataChangeHandler = ({ fieldName, inputValue }) => {
	accountFormData.value[fieldName] = inputValue
}

/*************************************************
*                 Requests Logic                 *
*************************************************/
const requestAccounts = async () => {
	showLoader.value = true
	applyErrorState.value = false

	try {
		await requestAccountsBalances()
		if (getAccountsBalances.value.length) {
			/* Setting Initial Selected Account */
			accountFormData.value.accountId = getAccountsBalances.value.find(account => account.default).id
		}
		showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error.response.data, requestAccounts)
	}
}

const amortizeCreditAccountHandler = async () => {
	showOverlayLoader.value = true
	showFeedbackToast.value = false
	applyErrorState.value = false

	const payload = {
		accountToAmortizeId: props.selectedCreditAccount.id,
		data: { sourceAccountId: accountFormData.value.accountId }
	}

	try {
		await amortizeCreditAccount(payload)
		showOverlayLoader.value = false
	} catch (error) {
		errorHandler(error.response.data, amortizeCreditAccountHandler)
	}
}

const partialAmortizeCreditAccountHandler = async () => {
	showOverlayLoader.value = true
	showFeedbackToast.value = false
	applyErrorState.value = false

	const payload = {
		accountToAmortizeId: props.selectedCreditAccount.id,
		data: {
            sourceAccountId: accountFormData.value.accountId,
            numberOfSettlements: accountFormData.value.numberOfSettlements
        }
	}

	try {
		await partialAmortizeCreditAccount(payload)
		showOverlayLoader.value = false
	} catch (error) {
		errorHandler(error.response.data, partialAmortizeCreditAccountHandler)
	}
}
/*************************************************
*             Success/Error Handlers             *
*************************************************/
const successHandler = data => {
    responseData.value = {
        ...data.data,
        errorLevel: data.errorLevel,
        code: data.code
    }
    showOperationsButtons.value = data.errorLevel === 'INFO' && data.data.hasReceipt

    headerOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !responseData.value.hasReceipt ? null
        : {
            iconsColor: 'var(--positive)',
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
            operationId: data.data.operationId
        }
    }

    goToStep(3)
}

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	showOverlayLoader.value = false
	showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*               Footer/Buttons Logic             *
*************************************************/
const footerButtonsParams = ref([
	{
		label: t('credits.myCredits.amortization.footerButtons.cancel'),
		buttonPosition: 'left',
		step: 1,
		callbackFunction: () => { navigateByName(routeList.CREDITS_MY_CREDITS_NAME, { selectedCardId: props.selectedCardId }) }
	}, {
		label: t('credits.myCredits.amortization.footerButtons.forward'),
		icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
		step: 1,
		callbackFunction: () => { activeStep.value = 2 }
	}, {
		label: t('credits.myCredits.amortization.footerButtons.edit'),
		buttonPosition: !getMobileAppState.value ? 'right' : 'left',
        icon: 'arrow-left-full',
		step: 2,
		callbackFunction: () => { activeStep.value = 1 }
	}, {
		label: t('credits.myCredits.amortization.footerButtons.validate'),
		icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
		step: 2,
		callbackFunction: amortizationTypeHandler
	}
])

requestAccounts()

onBeforeUnmount(() => {
	clearFeatureStates()
})

</script>

<style lang="scss" scoped>
</style>
